// ============================================================================
// passkeyDefi.js — Défi WebAuthn préparé AVANT le geste (10/09/2026)
// ============================================================================
// Pourquoi : sur iPhone, Safari n'autorise la demande biométrique que dans les
// quelques secondes qui suivent le geste de l'utilisateur. Le 9 septembre au
// soir, la fonction de défi a démarré à froid (3,9 s) et la cérémonie a été
// refusée par iOS ; la seconde tentative, servie en 0,2 s, a réussi. On
// prépare donc le défi à l'ouverture de l'écran de connexion et on le
// renouvelle tant que l'écran est visible : au geste, la biométrie part sans
// attendre le réseau.
//
// Règles : un défi vit 2 min côté serveur (usage unique, consommé par
// passkeyLogin même en cas d'échec) ; on le renouvelle avant sa péremption,
// seulement onglet visible, avec un plafond de renouvellements pour ne pas
// épuiser la limite par adresse IP (20 défis / 10 min). Un échec de la
// cérémonie côté navigateur (annulation, refus iOS) ne consomme pas le défi :
// la nouvelle tentative repart immédiatement avec le même.
import { REGION_FRANCFORT } from "./regionsFonctions";

const MARGE_MS = 15 * 1000;          // on considère le défi périmé 15 s avant l'heure serveur
const TTL_DEFAUT_MS = 2 * 60 * 1000;  // valeur serveur (CHALLENGE_TTL_MS) si expiresInSec est absent

let _cache = null;      // { challenge, expiresAt, obtenuLe } ou { promise }
let _demandeur = null;  // injection pour les tests

/** Tests uniquement : remplace l'appel réseau. */
export function _definirDemandeur(fn) { _demandeur = fn; _cache = null; }

const supporte = () => typeof window !== "undefined" && !!window.PublicKeyCredential;

async function demanderDefi() {
  if (_demandeur) return _demandeur();
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fn = getFunctions(undefined, REGION_FRANCFORT);
  const { data } = await httpsCallable(fn, "passkeyChallenge")({});
  return data;
}

/** Un défi valide est-il prêt en cache ? */
export function defiPret() {
  return !!(_cache && _cache.challenge && _cache.expiresAt - Date.now() > 0);
}

/** Demande (ou réutilise) un défi ; ne lance jamais deux demandes en parallèle. */
export function prechargerDefi() {
  if (!supporte()) return Promise.resolve(null);
  if (_cache && _cache.promise) return _cache.promise;
  if (defiPret()) return Promise.resolve(_cache);
  const p = demanderDefi().then((data) => {
    if (!data || !data.challenge) throw new Error("Défi indisponible — réessayez.");
    const ttl = Number(data.expiresInSec) > 0 ? Number(data.expiresInSec) * 1000 : TTL_DEFAUT_MS;
    const d = { challenge: data.challenge, expiresAt: Date.now() + ttl - MARGE_MS, obtenuLe: Date.now() };
    _cache = d;
    return d;
  }).catch((e) => { _cache = null; throw e; });
  _cache = { promise: p };
  return p;
}

/** Pour la cérémonie : le défi en cache s'il est prêt, sinon celui qui arrive, sinon un neuf. */
export async function obtenirDefi() {
  if (defiPret()) return _cache.challenge;
  if (_cache && _cache.promise) {
    try { const d = await _cache.promise; if (d && d.expiresAt - Date.now() > 0) return d.challenge; } catch { /* on redemande */ }
  }
  const d = await prechargerDefi();
  if (!d || !d.challenge) throw new Error("Défi indisponible — réessayez.");
  return d.challenge;
}

/** À appeler après passkeyLogin (succès ou refus) : le serveur l'a consommé. */
export function consommerDefi(challenge) {
  if (_cache && _cache.challenge && (!challenge || _cache.challenge === challenge)) _cache = null;
}

export function oublierDefi() { _cache = null; }

/**
 * Précharge à l'ouverture de l'écran de connexion et renouvelle tant que
 * l'onglet est visible. Retourne la fonction de désinstallation (useEffect).
 */
export function installerPrechargeDefi({ actif = true, intervalleMs = 90 * 1000, maxRenouvellements = 6 } = {}) {
  if (!actif || !supporte() || typeof document === "undefined") return () => {};
  let renouvellements = 0;
  let arrete = false;
  const tenter = () => {
    if (arrete || document.visibilityState === "hidden") return;
    if (defiPret()) return;
    if (renouvellements >= maxRenouvellements) return;
    renouvellements += 1;
    prechargerDefi().catch(() => {});
  };
  renouvellements = -1; // la première demande ne compte pas comme un renouvellement
  tenter();
  const timer = setInterval(tenter, intervalleMs);
  const surVisibilite = () => { if (document.visibilityState === "visible") tenter(); };
  document.addEventListener("visibilitychange", surVisibilite);
  return () => { arrete = true; clearInterval(timer); document.removeEventListener("visibilitychange", surVisibilite); };
}
